import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { DatastoreService } from './datastore.service';

/**
 * Stamps `booking.settings.payment_started` before a gateway redirect.
 *
 * payment-verify anchors its polling window on this stamp: a payment record
 * `created_at` after it means the payment the customer just made. Every flow
 * that redirects to a payment gateway must stamp first — and must NOT redirect
 * if the stamp fails, because verify reads the stamp back from the API-fetched
 * booking and treats a missing stamp as a failed payment, clearing the pending
 * follow-up state. Money taken with no stamp means the purchase is recorded
 * but whatever the payment was FOR is never applied (live incident: booking
 * 11271, 20 Aug 2026 — £49 of confetti paid for and never added).
 *
 * This replaces five per-component copies of the same method, all of which
 * swallowed a failed write and redirected anyway.
 */
@Injectable({ providedIn: 'root' })
export class PaymentStartService {
  constructor(
    private apiService: ApiService,
    private dataStore: DatastoreService,
  ) {}

  /**
   * Returns the updated booking when the stamp landed on JRNI, or `null` when
   * it did not — in which case the caller MUST abort the payment flow instead
   * of redirecting to the gateway. Fail before money is taken, not after.
   *
   * On failure the stamped settings are still written to the in-memory booking
   * and `localStorage['ceremony']` — payment-verify uses that copy as a backup
   * window, so even a flow that somehow reaches the gateway stays recoverable.
   */
  async stampPaymentStarted(booking: any): Promise<any | null> {
    if (!booking) return null;
    // Same format the per-component copies produced with
    // moment().utc().format('YYYY-MM-DDTHH:mm:ss[Z]').
    const paymentStarted = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
    const mergedSettings = { ...(booking.settings || {}), payment_started: paymentStarted };
    // api.service.updateBooking reads dataStore.company.id synchronously
    // (outside its try/catch) to set headers — populate it so the PUT fires.
    if (!this.dataStore.company) {
      this.dataStore.company = { id: booking.company_id } as any;
    }
    try {
      const updated: any = await this.apiService.updateBooking(
        { settings: mergedSettings },
        booking.company_id,
        booking.id,
        false,
      );
      if (updated && typeof updated !== 'string') {
        this.dataStore.booking = updated;
        localStorage.setItem('ceremony', JSON.stringify(updated));
        return updated;
      }
      // updateBooking swallows HTTP errors and returns the error message as a
      // string — that is a FAILED write, not a success with an odd shape.
      console.error('payment_started stamp did not persist on JRNI', updated);
      this.reportFailure(booking, String(updated));
    } catch (err) {
      console.error('Failed to stamp payment_started on booking', err);
      this.reportFailure(booking, err);
    }
    booking.settings = mergedSettings;
    this.dataStore.booking = booking;
    localStorage.setItem('ceremony', JSON.stringify(booking));
    return null;
  }

  // The pre-fix failures were console-only, so nobody knew they were happening
  // until a customer was charged with nothing applied (booking 11271). Surface
  // them in New Relic so production occurrences are visible and countable.
  private reportFailure(booking: any, cause: unknown): void {
    const error = cause instanceof Error ? cause : new Error(`payment_started stamp failed: ${cause}`);
    (window as any).newrelic?.noticeError(error, {
      bookingId: booking?.id,
      companyId: booking?.company_id,
    });
  }
}
